import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Pill, Plus, Archive, ArchiveRestore, Clock, Check, ChevronLeft, ChevronRight,
    Calendar as CalendarIcon, History as HistoryIcon, AlertTriangle, ChevronDown,
} from 'lucide-react';
import toast from 'react-hot-toast';
import residencesService from '../../../services/carehome_manager/residencesService';
import { Modal, Select, Button } from '../../../components';
import { useAuth } from '../../../context/AuthContext';
import {
    FREQUENCY_OPTIONS, FREQUENCY_TIMES, INSTRUCTION_OPTIONS, TimesEditor, normalizeTimeList,
} from './medicationTimes';
import {
    DAYS, ROUTE_OPTIONS, VERSION_STATUS_OPTIONS, LIFECYCLE_FOR_STATUS, EMPTY_VERSION,
    normalizePlan, resolvedVersions, versionAt, currentVersion, latestVersion,
    pendingVersions, pendingIndexes, isMedArchived, isScheduled, isPrn, courseText,
    scheduleText, statusInfo, dayDoses, countOutcomes, prnAvailable, entriesForDose,
    doseKeyOf, outcomeOf, nextScheduled, lastGiven, changedFields, versionSummary,
    allChanges, newId, nowStamp, todayISO, parseDate, dateISO, fmtDate, fmtMonth,
    fmtStamp, fmtClock, courseLength, OUTCOME_LABELS, OUTCOME_SHORT, OUTCOME_ORDER,
} from './medicationModel';

// Pill colouring, shared by the status badges and the calendar legend.
const PILL = {
    given: 'text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20',
    active: 'text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20',
    refused: 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20',
    hold: 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20',
    withheld: 'text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20',
    omitted: 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20',
    unrecorded: 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20',
    scheduled: 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
    upcoming: 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
    ended: 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-[#31373f]',
    archived: 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-[#31373f]',
};

const DOT = {
    given: 'bg-emerald-500', refused: 'bg-amber-500', withheld: 'bg-purple-500',
    omitted: 'bg-red-400', unrecorded: 'bg-red-400',
    scheduled: 'bg-transparent border-[1.5px] border-blue-500',
};

const inputClass = 'w-full h-10 border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#262b33] rounded-lg px-3 text-[13px] text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 disabled:bg-gray-100 dark:disabled:bg-[#20242c] disabled:text-gray-400';
const labelClass = 'block text-[12px] font-semibold text-gray-600 dark:text-gray-400 mb-1.5';

const FREQUENCY_LABEL = Object.fromEntries(FREQUENCY_OPTIONS.map((o) => [o.value, o.label]));

// The blank form. A new medication starts today and runs until it is changed.
const emptyForm = () => ({
    ...EMPTY_VERSION,
    times: [...FREQUENCY_TIMES.once],
    days: [...DAYS],
    instruction: 'Morning',
    start: todayISO(),
    end: '',
    ongoing: true,
    prnInterval: '', prnMaximum: '', prnWhen: '',
    effectiveDate: todayISO(),
    effectiveTime: nowStamp().slice(11),
    changeReason: '',
});

/** Form state -> the version that will be written to the plan. */
const formToVersion = (form, { editingId, now }) => {
    const prn = form.frequency === 'prn';
    return {
        name: form.name.trim(),
        strength: form.strength.trim(),
        dose: form.dose.trim(),
        route: form.route,
        category: form.category.trim(),
        instruction: form.instruction,
        frequency: form.frequency,
        status: form.status,
        times: prn ? [] : normalizeTimeList(form.times),
        days: prn ? [] : DAYS.filter((d) => form.days.includes(d)),
        start: form.start,
        end: form.ongoing ? '' : form.end,
        doctor: form.doctor.trim(),
        notes: form.notes.trim(),
        prn: prn ? {
            interval: Number(form.prnInterval) || 0,
            maximum: form.prnMaximum.trim(),
            when: form.prnWhen.trim(),
        } : null,
        effectiveFrom: editingId ? `${form.effectiveDate}T${form.effectiveTime}` : now,
        supersedesVersions: [],
    };
};

const MedicationTab = ({ residenceId, medical = {}, onSaved }) => {
    const { user } = useAuth();
    const who = (user && (user.name || user.email)) || 'Manager';

    const [meds, setMeds] = useState([]);
    const [administrations, setAdministrations] = useState([]);
    const [saving, setSaving] = useState(false);
    const [view, setView] = useState('plan'); // plan | calendar | archived | history

    // Calendar
    const [month, setMonth] = useState(() => parseDate(`${todayISO().slice(0, 7)}-01`));
    const [selectedDate, setSelectedDate] = useState(todayISO());

    // Shared filters (calendar + history), as in the reference
    const [filter, setFilter] = useState('all');
    const [historyDate, setHistoryDate] = useState('');
    const [page, setPage] = useState(0);
    const PER_PAGE = 10;

    // Form
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [restoreMode, setRestoreMode] = useState(false);
    const [formError, setFormError] = useState('');

    // Confirmation (archive)
    const [confirm, setConfirm] = useState(null); // { title, body, label, run }

    // One clock for the whole tab, so every version lookup in a render agrees
    // about what "current" means. Ticked each minute: a dose that falls due
    // while the page is open moves from Scheduled to No record on its own.
    const [now, setNow] = useState(nowStamp);
    const today = now.slice(0, 10);
    useEffect(() => {
        const timer = setInterval(() => setNow(nowStamp()), 60000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        setMeds(normalizePlan(medical.medication_plan, medical.medication_history));
    }, [medical]);

    const loadAdministrations = useCallback(async () => {
        try {
            const res = await residencesService.getMedicationAdministrations(residenceId);
            setAdministrations(res.data || []);
        } catch (e) {
            // non-fatal — the schedule still reads without the dose log
        }
    }, [residenceId]);

    useEffect(() => { if (residenceId) loadAdministrations(); }, [residenceId, loadAdministrations]);
    useEffect(() => { setPage(0); }, [view, filter, historyDate, administrations.length]);

    const activeMeds = useMemo(() => meds.filter((m) => !isMedArchived(m, now)), [meds, now]);
    const archivedMeds = useMemo(() => meds.filter((m) => isMedArchived(m, now)), [meds, now]);

    // -----------------------------------------------------------------------
    // Saving
    // -----------------------------------------------------------------------

    // `medication_history` stays the one-row-per-medicine lifecycle list the
    // mobile app's medical-history screen reads. It is rebuilt from the plan on
    // every save so the two can never drift apart.
    const lifecycleFor = (list) => list.map((m) => {
        const v = currentVersion(m, now) || latestVersion(m) || {};
        return {
            id: m.id,
            name: v.name || '',
            category: v.category || 'General',
            date: v.start || today,
            status: LIFECYCLE_FOR_STATUS[v.status] || 'Inprogress',
        };
    });

    const persist = async (newMeds, successMessage) => {
        setSaving(true);
        try {
            await residencesService.updateResidence(residenceId, {
                medicalDetails: {
                    medication_plan: newMeds,
                    medication_history: lifecycleFor(newMeds),
                },
            });
            setMeds(newMeds);
            if (onSaved) onSaved();
            toast.success(successMessage || 'Medication saved');
            return true;
        } catch (e) {
            console.error(e);
            toast.error('Failed to save medication');
            return false;
        } finally {
            setSaving(false);
        }
    };

    // -----------------------------------------------------------------------
    // Form
    // -----------------------------------------------------------------------

    const openAdd = () => {
        setForm(emptyForm());
        setEditingId(null);
        setRestoreMode(false);
        setFormError('');
        setShowForm(true);
    };

    const openEdit = (med, restoring = false) => {
        const v = latestVersion(med) || EMPTY_VERSION;
        // A pending update is edited in place: the form opens on the time it was
        // already going to take effect, and saving replaces it.
        const pending = pendingVersions(med, now);
        const effective = pending.length ? pending.at(-1).effectiveFrom : nowStamp();
        setForm({
            ...emptyForm(),
            ...v,
            times: v.times && v.times.length ? [...v.times] : [...FREQUENCY_TIMES.once],
            days: v.days && v.days.length ? [...v.days] : [...DAYS],
            status: restoring ? 'active' : (v.status === 'archived' ? 'active' : v.status),
            ongoing: !v.end,
            prnInterval: v.prn ? String(v.prn.interval || '') : '',
            prnMaximum: v.prn ? v.prn.maximum || '' : '',
            prnWhen: v.prn ? v.prn.when || '' : '',
            effectiveDate: effective.slice(0, 10),
            effectiveTime: effective.slice(11),
            changeReason: '',
        });
        setEditingId(med.id);
        setRestoreMode(restoring);
        setFormError('');
        setShowForm(true);
    };

    const setField = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

    const changeFrequency = (frequency) => setForm((f) => ({
        ...f,
        frequency,
        times: FREQUENCY_TIMES[frequency] ? [...FREQUENCY_TIMES[frequency]] : f.times,
        days: frequency === 'weekly' ? [f.days[0] || DAYS[0]] : f.days,
    }));

    // Editing the times by hand means the schedule no longer matches a preset.
    const changeTimes = (times) => setForm((f) => ({
        ...f,
        times,
        frequency: f.frequency === 'prn' ? 'prn' : 'custom',
    }));

    const toggleDay = (day) => setForm((f) => ({
        ...f,
        days: f.frequency === 'weekly'
            ? [day]
            : (f.days.includes(day) ? f.days.filter((d) => d !== day) : [...f.days, day]),
    }));

    const editingMed = editingId ? meds.find((m) => String(m.id) === String(editingId)) : null;

    // Live "what will this change do" preview for an edit.
    const preview = useMemo(() => {
        if (!editingMed) return null;
        const draft = formToVersion(form, { editingId, now });
        draft.supersedesVersions = pendingIndexes(editingMed, now);
        const given = lastGiven(administrations, editingId, now);
        const base = { given, draft };

        if (!/^\d{4}-\d{2}-\d{2}$/.test(form.effectiveDate) || !/^\d{2}:\d{2}$/.test(form.effectiveTime) || !draft.start) {
            return { ...base, message: 'Choose a valid effective date and time.' };
        }
        if (draft.effectiveFrom < now) return { ...base, message: 'Choose a time from now onwards.' };
        if (draft.end && draft.end < draft.start) return { ...base, message: 'Check the medication course dates.' };
        if (draft.frequency !== 'prn' && (!draft.times.length || !draft.days.length)) {
            return { ...base, message: 'Complete the administration times and days.' };
        }

        const proposal = { id: editingMed.id, versions: [...editingMed.versions, draft] };
        const next = nextScheduled(proposal, now, administrations);
        const firstUpdated = nextScheduled(proposal, draft.effectiveFrom, administrations, draft);

        let schedule = '';
        if (next) schedule = next.version === draft ? 'Updated schedule' : 'Existing schedule, before the change takes effect';
        else if (draft.status === 'hold') schedule = 'Medication will be on hold.';
        else if (draft.end) schedule = 'No further doses within the course.';

        // A change that moves a dose later in the day can quietly add a second
        // dose on the changeover day — say so before it is saved.
        let transition = '';
        if (firstUpdated) {
            const wasScheduled = isScheduled(
                versionAt(editingMed, `${firstUpdated.date}T${firstUpdated.time}`),
                firstUpdated.date, firstUpdated.time,
            );
            const givenSameDay = given && given.dose_date === firstUpdated.date;
            const earlierTimes = [...new Set(resolvedVersions(proposal).flatMap((e) => e.version.times || []))]
                .filter((time) => {
                    const old = versionAt(proposal, `${firstUpdated.date}T${time}`);
                    return time < firstUpdated.time && old !== draft && isScheduled(old, firstUpdated.date, time);
                })
                .sort();
            if (!wasScheduled && (givenSameDay || earlierTimes.length)) {
                transition = `${givenSameDay
                    ? `A dose is already recorded as given at ${given.scheduled_time || fmtClock(given.recorded_at)}`
                    : `The existing schedule also includes ${earlierTimes.join(', ')}`} on ${fmtDate(firstUpdated.date)}. This change adds a dose at ${firstUpdated.time} on the same day. Check the prescribed timing before saving.`;
            }
        }

        return {
            ...base,
            next: next ? fmtStamp(next.stamp) : (draft.frequency === 'prn' ? 'As required · no scheduled time' : 'No scheduled doses'),
            schedule,
            firstUpdated: (next && (!firstUpdated || next.stamp !== firstUpdated.stamp))
                ? (firstUpdated ? fmtStamp(firstUpdated.stamp) : (draft.frequency === 'prn' ? 'As required · no scheduled time' : 'No scheduled doses'))
                : null,
            transition,
        };
    }, [editingMed, editingId, form, administrations, now]);

    const validate = () => {
        const prn = form.frequency === 'prn';
        if (!form.name.trim()) return 'Enter the medicine name.';
        if (!form.strength.trim()) return 'Enter the strength and form.';
        if (!form.dose.trim()) return 'Enter the dose to give.';
        if (!/[a-zA-Zµμ]/.test(form.strength) || !/[a-zA-Zµμ]/.test(form.dose)) {
            return 'Include units and form in the strength, and an amount with a unit in Dose to give. A number on its own is not enough.';
        }
        if (!form.route) return 'Select the route.';
        if (!form.start || (!form.ongoing && !form.end)) return 'Enter the course dates, or choose Ongoing.';
        if (!form.ongoing && form.end < form.start) return 'End date cannot be earlier than start date.';
        if (restoreMode && !form.ongoing && form.end < today) {
            return 'This course has ended. Review its dates before restoring it.';
        }
        if (!prn) {
            const times = form.times.filter(Boolean);
            if (!form.days.length) return 'Select at least one administration day.';
            if (!times.length || times.length !== form.times.length) return 'Enter each administration time.';
            if (new Set(times).size !== times.length) return 'Each administration time must be different.';
            if (form.frequency === 'weekly' && form.days.length !== 1) {
                return 'Choose one day for a weekly schedule, or select Custom.';
            }
        } else if (!Number(form.prnInterval) || !form.prnMaximum.trim() || !form.prnWhen.trim()) {
            return 'Complete the as-required dose instructions.';
        }
        if (editingId) {
            if (!form.changeReason.trim()) return 'Enter a reason for this change.';
            if (!form.effectiveDate || !form.effectiveTime) return 'Choose when this change takes effect.';
            if (`${form.effectiveDate}T${form.effectiveTime}` < now) {
                return 'The effective date and time cannot be in the past. Previous records must be retained.';
            }
        }
        return '';
    };

    const handleSave = async () => {
        const problem = validate();
        if (problem) { setFormError(problem); return; }
        setFormError('');

        const version = formToVersion(form, { editingId, now });
        let newMeds;
        let action;

        if (editingMed) {
            const previous = latestVersion(editingMed);
            version.supersedesVersions = pendingIndexes(editingMed, now);
            const scheduled = version.effectiveFrom > now;
            action = restoreMode
                ? (scheduled ? 'Medication restore scheduled' : 'Medication restored')
                : (scheduled ? 'Medication update scheduled' : 'Medication updated');
            const change = {
                id: newId(),
                name: version.name,
                action,
                at: now,
                effectiveAt: version.effectiveFrom,
                replacesPending: version.supersedesVersions.length,
                who,
                reason: form.changeReason.trim(),
                before: versionSummary(previous),
                after: versionSummary(version),
                fields: changedFields(previous, version),
            };
            newMeds = meds.map((m) => (String(m.id) === String(editingId)
                ? { ...m, versions: [...m.versions, version], changes: [...(m.changes || []), change] }
                : m));
        } else {
            action = 'Medication added';
            const id = newId();
            newMeds = [...meds, {
                id,
                versions: [version],
                changes: [{
                    id: newId(), name: version.name, action, at: now,
                    effectiveAt: version.effectiveFrom, replacesPending: 0, who,
                    reason: 'New medication schedule.', before: '',
                    after: versionSummary(version), fields: [],
                }],
            }];
        }

        const ok = await persist(newMeds, `${action}.`);
        if (!ok) return;
        setShowForm(false);
        setEditingId(null);
        setRestoreMode(false);
        if (restoreMode) setView('plan');
    };

    // Medications are never deleted — a client's medication record has to stay
    // auditable. Archiving adds an "archived" version from now on: the med
    // leaves the carers' rounds, every dose already recorded against it stays.
    const archiveMed = (med) => {
        const v = currentVersion(med, now) || latestVersion(med);
        setConfirm({
            title: 'Archive medication?',
            body: [
                `${v.name} will be removed from future rounds.`,
                'Previous administration records and schedule versions will stay in History.',
            ],
            label: 'Archive',
            run: async () => {
                const archived = {
                    ...v,
                    status: 'archived',
                    effectiveFrom: now,
                    supersedesVersions: pendingIndexes(med, now),
                };
                const change = {
                    id: newId(), name: v.name, action: 'Medication archived', at: now,
                    effectiveAt: now, replacesPending: archived.supersedesVersions.length, who,
                    reason: 'Removed from future rounds; previous records retained.',
                    before: versionSummary(v), after: versionSummary(archived), fields: [],
                };
                await persist(
                    meds.map((m) => (String(m.id) === String(med.id)
                        ? { ...m, versions: [...m.versions, archived], changes: [...(m.changes || []), change] }
                        : m)),
                    'Medication archived. Previous records retained.',
                );
                setConfirm(null);
            },
        });
    };

    const medicineOptions = useMemo(() => [
        { value: 'all', label: 'All medicines' },
        ...meds.map((m) => {
            const v = currentVersion(m, now) || {};
            return { value: String(m.id), label: `${v.name}${v.status === 'archived' ? ' (archived)' : ''}` };
        }),
    ], [meds, now]);

    const openDayInHistory = (date) => { setHistoryDate(date); setView('history'); };

    return (
        <div className="mt-6 animate-in fade-in duration-300">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-5">
                <div>
                    <h2 className="text-[18px] font-semibold text-gray-800 dark:text-gray-100">Medication</h2>
                    <p className="text-[13px] text-gray-500 dark:text-gray-400">Manage the medication schedule and review the full history.</p>
                </div>
                <button onClick={openAdd}
                    className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition shadow-sm shrink-0">
                    <Plus className="w-4 h-4" /> Add medication
                </button>
            </div>

            {/* View toggle */}
            <div className="inline-flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1 mb-5 max-w-full overflow-x-auto">
                {[
                    ['plan', 'Medications', activeMeds.length],
                    ['calendar', 'Calendar', null],
                    ['archived', 'Archived', archivedMeds.length],
                    ['history', 'History', null],
                ].map(([key, label, count]) => (
                    <button key={key} onClick={() => setView(key)}
                        className={`px-4 py-1.5 rounded-md text-[13px] font-medium whitespace-nowrap transition ${view === key ? 'bg-white dark:bg-[#262b33] text-blue-600 shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}>
                        {label}{count ? ` (${count})` : ''}
                    </button>
                ))}
            </div>

            {view === 'plan' && (
                activeMeds.length === 0 ? (
                    <EmptyState title="No medicines added"
                        text="Use Add medication to enter a medicine and its course." />
                ) : (
                    <>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            {activeMeds.map((med) => (
                                <MedCard key={med.id} med={med} now={now} today={today}
                                    onEdit={() => openEdit(med)}
                                    onArchive={() => archiveMed(med)} />
                            ))}
                        </div>
                        <button onClick={() => setView('calendar')}
                            className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-semibold text-blue-600 hover:text-blue-700">
                            <CalendarIcon className="w-3.5 h-3.5" /> View all medicines in Calendar
                        </button>
                    </>
                )
            )}

            {view === 'calendar' && (
                <CalendarView
                    meds={meds} records={administrations} now={now} today={today}
                    month={month} onMonth={setMonth}
                    selectedDate={selectedDate} onSelectDate={setSelectedDate}
                    filter={filter} onFilter={setFilter} medicineOptions={medicineOptions}
                    onDayHistory={openDayInHistory}
                />
            )}

            {view === 'archived' && (
                archivedMeds.length === 0 ? (
                    <EmptyState title="No archived medicines"
                        text="Archived medicines will appear here. They are never deleted — their dose history stays under History." />
                ) : (
                    <>
                        <p className="text-[12px] text-gray-400 dark:text-gray-500 mb-3">
                            Archived medicines no longer appear in future rounds. Their previous records stay in History.
                        </p>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            {archivedMeds.map((med) => (
                                <MedCard key={med.id} med={med} archived now={now} today={today}
                                    onRestore={() => openEdit(med, true)} />
                            ))}
                        </div>
                    </>
                )
            )}

            {view === 'history' && (
                <HistoryView
                    meds={meds} records={administrations} now={now}
                    filter={filter} onFilter={setFilter} medicineOptions={medicineOptions}
                    historyDate={historyDate} onHistoryDate={setHistoryDate}
                    page={page} perPage={PER_PAGE} onPage={setPage}
                />
            )}

            {/* Add / Edit / Restore */}
            <Modal
                isOpen={showForm}
                onClose={() => setShowForm(false)}
                title={restoreMode ? 'Review & restore medication' : editingId ? 'Edit medication' : 'Add medication'}
                maxWidth="max-w-[640px]"
                closeOnBackdrop
                footer={(
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
                        <Button variant="primary" leftIcon={<Check className="w-4 h-4" />} loading={saving} onClick={handleSave}>
                            {restoreMode ? 'Restore' : editingId ? 'Save changes' : 'Add'}
                        </Button>
                    </div>
                )}
            >
                <MedicationForm
                    form={form} setForm={setForm} setField={setField}
                    editingId={editingId} restoreMode={restoreMode}
                    editingMed={editingMed} now={now} today={today}
                    error={formError} preview={preview}
                    onFrequency={changeFrequency} onTimes={changeTimes} onToggleDay={toggleDay}
                />
            </Modal>

            {/* Confirm (archive) */}
            <Modal
                isOpen={!!confirm}
                onClose={() => setConfirm(null)}
                title={confirm ? confirm.title : ''}
                maxWidth="max-w-[420px]"
                closeOnBackdrop
                footer={(
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={() => setConfirm(null)}>Cancel</Button>
                        <Button variant="primary" loading={saving} onClick={() => confirm && confirm.run()}>
                            {confirm ? confirm.label : ''}
                        </Button>
                    </div>
                )}
            >
                <div className="space-y-2 text-[13px] text-gray-600 dark:text-gray-300">
                    {(confirm ? confirm.body : []).map((line) => <p key={line}>{line}</p>)}
                </div>
            </Modal>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function EmptyState({ title, text }) {
    return (
        <div className="bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-800 rounded-xl p-10 text-center">
            <Pill className="w-7 h-7 mx-auto mb-2.5 text-gray-300 dark:text-gray-600" />
            {title && <h3 className="text-[14px] font-semibold text-gray-700 dark:text-gray-200 mb-1">{title}</h3>}
            <p className="text-[13px] text-gray-500 dark:text-gray-400 max-w-md mx-auto">{text}</p>
        </div>
    );
}

function Badge({ kind, label }) {
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold whitespace-nowrap ${PILL[kind] || PILL.ended}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current" />
            {label || OUTCOME_LABELS[kind] || kind}
        </span>
    );
}

function Field({ label, value, onChange, placeholder, type = 'text', disabled, min, max, step, className = '', list }) {
    return (
        <div className={className}>
            {label && <label className={labelClass}>{label}</label>}
            <input type={type} value={value} list={list} disabled={disabled} min={min} max={max} step={step}
                onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inputClass} />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Medication card
// ---------------------------------------------------------------------------

function MedCard({ med, archived = false, now, today, onEdit, onArchive, onRestore }) {
    const v = currentVersion(med, now) || latestVersion(med);
    if (!v) return null;
    const [statusKey, statusLabel] = statusInfo(v, today);
    const future = pendingVersions(med, now).at(-1);

    return (
        <div className={`border rounded-xl p-4 shadow-sm ${archived
            ? 'bg-gray-50 dark:bg-[#20242c] border-gray-200 dark:border-gray-800'
            : 'bg-white dark:bg-[#262b33] border-gray-100 dark:border-gray-800'}`}>
            <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${archived
                    ? 'bg-gray-100 dark:bg-[#31373f]' : 'bg-blue-50 dark:bg-blue-900/30'}`}>
                    <Pill className={`w-5 h-5 ${archived ? 'text-gray-400 dark:text-gray-500' : 'text-blue-500'}`} />
                </div>

                <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                        <h4 className="text-[14px] font-bold text-gray-900 dark:text-gray-100">{v.name}</h4>
                        {v.strength && <span className="text-[12px] text-gray-500 dark:text-gray-400">{v.strength}</span>}
                    </div>
                    <p className="text-[12px] text-gray-600 dark:text-gray-400 mt-0.5">
                        {[v.dose, v.route, v.category, v.instruction].filter(Boolean).join(' · ')}
                    </p>

                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        {isPrn(v) ? (
                            <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                                <Clock className="w-3 h-3" /> As required
                            </span>
                        ) : (v.times || []).map((t) => (
                            <span key={t} className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-md px-1.5 py-0.5 border ${archived
                                ? 'text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-[#31373f] border-gray-200 dark:border-gray-700'
                                : 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-900/50'}`}>
                                <Clock className="w-3 h-3" /> {t}
                            </span>
                        ))}
                        <span className="text-[11px] text-gray-400 dark:text-gray-500">{scheduleText(v)}</span>
                    </div>

                    <div className="flex items-center gap-1.5 mt-2 text-[12px] text-gray-500 dark:text-gray-400">
                        <CalendarIcon className="w-3.5 h-3.5" /> {courseText(v)}
                    </div>

                    {v.prn && isPrn(v) && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5 bg-purple-50/60 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-900/30 rounded-lg px-2.5 py-1.5">
                            Min {v.prn.interval}h between doses · max {v.prn.maximum} in 24h. {v.prn.when}
                        </p>
                    )}

                    {future && (
                        <p className="inline-flex items-center gap-1.5 text-[11px] text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-md px-2 py-1 mt-2">
                            <Clock className="w-3 h-3" />
                            {archived ? 'Restore' : 'Update'} scheduled: {fmtStamp(future.effectiveFrom)}
                        </p>
                    )}
                </div>

                {!archived && (
                    <button onClick={onArchive} title="Archive this medication"
                        className="text-gray-300 dark:text-gray-600 hover:text-blue-600 transition shrink-0">
                        <Archive className="w-4 h-4" />
                    </button>
                )}
            </div>

            <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-gray-50 dark:border-gray-800">
                <Badge kind={statusKey} label={statusLabel} />
                {archived ? (
                    <button onClick={onRestore}
                        className="inline-flex items-center gap-1 text-[12px] font-semibold text-blue-600 hover:text-blue-700">
                        <ArchiveRestore className="w-3.5 h-3.5" /> Review &amp; restore
                    </button>
                ) : (
                    <button onClick={onEdit} className="text-[12px] font-semibold text-blue-600 hover:text-blue-700">Edit</button>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------

function StatDots({ counts, short = false }) {
    const keys = OUTCOME_ORDER.filter((k) => counts[k]);
    if (!keys.length) return <span className="text-[11px] text-gray-300 dark:text-gray-600">—</span>;
    return (
        <span className="flex flex-col gap-0.5">
            {keys.map((k) => (
                <span key={k} className="flex items-center gap-1 text-[10px] leading-tight text-gray-500 dark:text-gray-400">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${DOT[k]}`} />
                    <span>{counts[k]}</span>
                    <span className="truncate">{short ? OUTCOME_SHORT[k] : OUTCOME_LABELS[k].toLowerCase()}</span>
                </span>
            ))}
        </span>
    );
}

function CalendarView({
    meds, records, now, today, month, onMonth, selectedDate, onSelectDate,
    filter, onFilter, medicineOptions, onDayHistory,
}) {
    const year = month.getUTCFullYear();
    const monthNo = month.getUTCMonth();
    const daysInMonth = new Date(Date.UTC(year, monthNo + 1, 0)).getUTCDate();
    const offset = (new Date(Date.UTC(year, monthNo, 1)).getUTCDay() + 6) % 7;
    const cells = Math.ceil((offset + daysInMonth) / 7) * 7;

    const shiftMonth = (delta) => {
        const next = new Date(Date.UTC(year, monthNo + delta, 1));
        onMonth(next);
        onSelectDate(dateISO(next));
    };

    const goToday = () => {
        onMonth(parseDate(`${today.slice(0, 7)}-01`));
        onSelectDate(today);
    };

    const doses = useMemo(
        () => dayDoses(meds, records, selectedDate, filter, now),
        [meds, records, selectedDate, filter, now],
    );
    const counts = countOutcomes(doses);
    const recorded = doses.filter((d) => d.record).length;
    const prns = prnAvailable(meds, selectedDate, filter);

    return (
        <div>
            <div className="bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-800 rounded-xl p-4 shadow-sm">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                    <div className="flex items-center gap-2">
                        <button onClick={() => shiftMonth(-1)} aria-label="Previous month"
                            className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <h3 className="text-[15px] font-semibold text-gray-800 dark:text-gray-100 min-w-[140px] text-center">{fmtMonth(month)}</h3>
                        <button onClick={() => shiftMonth(1)} aria-label="Next month"
                            className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                            <ChevronRight className="w-4 h-4" />
                        </button>
                        <button onClick={goToday}
                            className="px-3 h-9 rounded-lg border border-gray-200 dark:border-gray-700 text-[12px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                            Today
                        </button>
                    </div>
                    <Select value={filter} onChange={onFilter} options={medicineOptions} size="sm" className="w-[220px]" />
                </div>

                <p className="text-[12px] text-gray-400 dark:text-gray-500 mb-3">
                    Select a date to see scheduled doses and recorded outcomes.
                </p>

                <div className="grid grid-cols-7 border-l border-t border-gray-100 dark:border-gray-800 rounded-lg overflow-hidden">
                    {DAYS.map((d) => (
                        <div key={d} className="bg-gray-50 dark:bg-[#20242c] text-center text-[11px] font-semibold text-gray-400 dark:text-gray-500 py-1.5 border-r border-b border-gray-100 dark:border-gray-800">
                            {d}
                        </div>
                    ))}
                    {Array.from({ length: cells }, (_, i) => {
                        const dayNo = i - offset + 1;
                        if (dayNo < 1 || dayNo > daysInMonth) {
                            return <div key={`blank-${i}`} className="min-h-[80px] bg-gray-50/50 dark:bg-[#20242c]/50 border-r border-b border-gray-100 dark:border-gray-800" />;
                        }
                        const date = dateISO(new Date(Date.UTC(year, monthNo, dayNo)));
                        const dayCounts = countOutcomes(dayDoses(meds, records, date, filter, now));
                        const isSelected = date === selectedDate;
                        return (
                            <button key={date} onClick={() => onSelectDate(date)}
                                className={`min-h-[80px] text-left px-2.5 py-2 border-r border-b border-gray-100 dark:border-gray-800 transition ${isSelected
                                    ? 'bg-blue-50/50 dark:bg-blue-900/10 ring-2 ring-inset ring-blue-500'
                                    : 'bg-white dark:bg-[#262b33] hover:bg-blue-50/40 dark:hover:bg-gray-800/40'}`}>
                                <span className="flex items-center justify-between gap-1 mb-1">
                                    <span className="text-[12px] font-semibold text-gray-700 dark:text-gray-200">{dayNo}</span>
                                    {date === today && (
                                        <span className="text-[9px] leading-[16px] px-1.5 rounded bg-blue-600 text-white">Today</span>
                                    )}
                                </span>
                                <StatDots counts={dayCounts} />
                            </button>
                        );
                    })}
                </div>

                <div className="flex flex-wrap gap-4 mt-3">
                    {['given', 'refused', 'withheld', 'omitted', 'unrecorded', 'scheduled'].map((k) => (
                        <span key={k} className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                            <span className={`w-1.5 h-1.5 rounded-full ${DOT[k]}`} /> {OUTCOME_LABELS[k]}
                        </span>
                    ))}
                </div>
            </div>

            {/* Selected date */}
            <div className="bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-800 rounded-xl p-4 shadow-sm mt-4">
                <div className="flex items-start justify-between gap-3 pb-3 border-b border-gray-100 dark:border-gray-800">
                    <h3 className="text-[14px] font-semibold text-gray-800 dark:text-gray-100">{fmtDate(selectedDate, true)}</h3>
                    <span className="text-[12px] text-gray-400 dark:text-gray-500 text-right">
                        {recorded ? `${recorded} recorded · ${counts.given || 0} given` : `${doses.length} scheduled`}
                    </span>
                </div>

                {doses.length === 0 ? (
                    <p className="py-6 text-center text-[12px] text-gray-400 dark:text-gray-500">
                        No scheduled doses for this date{filter !== 'all' ? ' and medicine' : ''}.
                    </p>
                ) : (
                    <div className="divide-y divide-gray-50 dark:divide-gray-800">
                        {doses.map((d) => (
                            <div key={d.key} className="grid grid-cols-1 sm:grid-cols-[64px_minmax(0,1fr)_auto] lg:grid-cols-[64px_minmax(0,1fr)_110px_minmax(0,210px)] items-start gap-3 py-3.5">
                                <span className="inline-flex justify-center text-[12px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-md px-2 py-1 w-fit">
                                    {d.time || '—'}
                                </span>
                                <div className="min-w-0">
                                    <p className="text-[12px] font-semibold text-gray-800 dark:text-gray-100">
                                        {(d.version && d.version.name) || 'Medicine'}
                                        {d.version && d.version.strength ? ` · ${d.version.strength}` : ''}
                                    </p>
                                    {d.version && (
                                        <>
                                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                                                {[d.version.dose, d.version.route].filter(Boolean).join(' · ')}
                                            </p>
                                            <p className="text-[11px] text-gray-400 dark:text-gray-500">{courseText(d.version)}</p>
                                        </>
                                    )}
                                </div>
                                <div className="lg:justify-self-start"><Badge kind={d.outcome} /></div>
                                <div className="text-[11px] text-gray-400 dark:text-gray-500 leading-relaxed sm:col-span-2 lg:col-span-1">
                                    {d.record ? (
                                        <>
                                            {fmtClock(d.record.recorded_at) && (
                                                <span className="block text-gray-600 dark:text-gray-300">
                                                    Recorded {fmtClock(d.record.recorded_at)}
                                                </span>
                                            )}
                                            {d.record.recorded_by_name && <span className="block">{d.record.recorded_by_name}</span>}
                                            {d.entries > 1 && <span className="block">{d.entries} entries kept</span>}
                                            {d.record.note && <span className="block">{d.record.note}</span>}
                                        </>
                                    ) : d.outcome === 'scheduled'
                                        ? `Scheduled for ${d.time}`
                                        : 'No administration record'}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {prns.length > 0 && (
                    <p className="text-[12px] text-gray-400 dark:text-gray-500 mt-3">
                        {prns.length} as-required medicine{prns.length === 1 ? '' : 's'} available · not included in scheduled totals.
                    </p>
                )}

                <div className="flex justify-end mt-2">
                    <button onClick={() => onDayHistory(selectedDate)}
                        className="text-[12px] font-semibold text-blue-600 hover:text-blue-700">
                        View this date in History
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

function HistoryView({
    meds, records, now, filter, onFilter, medicineOptions,
    historyDate, onHistoryDate, page, perPage, onPage,
}) {
    const [showChanges, setShowChanges] = useState(false);

    // One group per dose, newest first. A dose recorded more than once keeps
    // every entry — the latest stands, the rest fold away as earlier entries.
    const groups = useMemo(() => {
        const seen = new Set();
        const out = [];
        records
            .filter((r) => (filter === 'all' || String(r.plan_id) === String(filter))
                && (!historyDate || r.dose_date === historyDate))
            .forEach((r) => {
                const key = doseKeyOf(r);
                if (seen.has(key)) return;
                seen.add(key);
                out.push(entriesForDose(records, key));
            });
        return out.sort((a, b) => {
            const x = a.at(-1);
            const y = b.at(-1);
            return String(y.dose_date).localeCompare(String(x.dose_date))
                || String(y.recorded_at).localeCompare(String(x.recorded_at));
        });
    }, [records, filter, historyDate]);

    const changes = useMemo(() => allChanges(meds, filter, historyDate), [meds, filter, historyDate]);

    const total = groups.length;
    const totalPages = Math.max(1, Math.ceil(total / perPage));
    const safePage = Math.min(page, totalPages - 1);
    const start = safePage * perPage;
    const pageGroups = groups.slice(start, start + perPage);

    const versionFor = (rec) => {
        const med = meds.find((m) => String(m.id) === String(rec.plan_id));
        return med ? versionAt(med, `${rec.dose_date}T${rec.scheduled_time || '23:59'}`) : null;
    };

    return (
        <div>
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <div>
                    <h3 className="text-[14px] font-semibold text-gray-700 dark:text-gray-300">Daily medication status</h3>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 max-w-md">
                        Original records and earlier entries are retained. Each scheduled dose is counted once.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Select value={filter} onChange={onFilter} options={medicineOptions} size="sm" className="w-[200px]" />
                    <input type="date" value={historyDate} onChange={(e) => onHistoryDate(e.target.value)}
                        className="h-9 border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#262b33] rounded-lg px-2.5 text-[12px] text-gray-600 dark:text-gray-300" />
                    {historyDate && (
                        <button onClick={() => onHistoryDate('')}
                            className="h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-700 text-[12px] text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                            All dates
                        </button>
                    )}
                </div>
            </div>

            {total === 0 ? (
                <EmptyState title="No recorded doses"
                    text="Scheduled doses appear in Calendar. Recorded outcomes will appear here." />
            ) : (
                <div className="space-y-4">
                    {groupByDate(pageGroups).map((group) => (
                        <div key={group.date}>
                            <p className="text-[12px] font-semibold text-gray-500 dark:text-gray-400 mb-2">{fmtDate(group.date)}</p>
                            <div className="bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-800 rounded-xl divide-y divide-gray-50 dark:divide-gray-800 px-4">
                                {group.entries.map((entries) => (
                                    <DoseHistoryItem key={doseKeyOf(entries.at(-1))} entries={entries} versionFor={versionFor} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                    <span className="text-[12px] text-gray-400 dark:text-gray-500">
                        Showing {start + 1}–{Math.min(start + perPage, total)} of {total} doses
                    </span>
                    <div className="flex items-center gap-2">
                        <button onClick={() => onPage(safePage - 1)} disabled={safePage === 0}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-[12px] text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                            <ChevronLeft className="w-3.5 h-3.5" /> Prev
                        </button>
                        <span className="text-[12px] font-medium text-gray-600 dark:text-gray-300">Page {safePage + 1} of {totalPages}</span>
                        <button onClick={() => onPage(safePage + 1)} disabled={safePage >= totalPages - 1}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-[12px] text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                            Next <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            )}

            {/* Medication changes — every edit, archive and restore ever saved */}
            <div className="bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-800 rounded-xl mt-6">
                <button onClick={() => setShowChanges((s) => !s)}
                    className="w-full flex items-center justify-between gap-2 px-4 py-3.5 text-left">
                    <span className="flex items-center gap-2 text-[13px] font-semibold text-gray-700 dark:text-gray-200">
                        <HistoryIcon className="w-4 h-4 text-gray-400" /> Medication changes
                        <span className="text-[12px] font-normal text-gray-400 dark:text-gray-500">({changes.length})</span>
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition ${showChanges ? 'rotate-180' : ''}`} />
                </button>
                {showChanges && (
                    <div className="px-4 pb-4">
                        {changes.length === 0 ? (
                            <p className="text-[12px] text-gray-400 dark:text-gray-500">No medication changes for this selection.</p>
                        ) : (
                            <div className="divide-y divide-gray-50 dark:divide-gray-800">
                                {changes.map((c) => <ChangeRow key={c.id} change={c} />)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/** Dose groups on this page, bucketed under their date heading. */
function groupByDate(pageGroups) {
    const out = [];
    pageGroups.forEach((entries) => {
        const date = entries.at(-1).dose_date;
        let group = out.find((g) => g.date === date);
        if (!group) { group = { date, entries: [] }; out.push(group); }
        group.entries.push(entries);
    });
    return out;
}

function DoseHistoryItem({ entries, versionFor }) {
    const [open, setOpen] = useState(false);
    const latest = entries.at(-1);
    const earlier = entries.slice(0, -1).reverse();

    return (
        <div className="py-3.5">
            <RecordBody rec={latest} versionFor={versionFor} />
            {earlier.length > 0 && (
                <div className="mt-2.5">
                    <button onClick={() => setOpen((o) => !o)}
                        className="text-[11px] text-gray-400 dark:text-gray-500 hover:text-blue-600 transition">
                        {earlier.length} earlier entr{earlier.length === 1 ? 'y' : 'ies'} · view history
                    </button>
                    {open && (
                        <div className="mt-2 space-y-2">
                            {earlier.map((rec) => (
                                <div key={rec.id} className="border-l-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#20242c] rounded-r-md pl-3 pr-3 py-2.5">
                                    <RecordBody rec={rec} versionFor={versionFor} earlier />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function RecordBody({ rec, versionFor, earlier = false }) {
    const v = versionFor(rec) || {};
    return (
        <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 text-[12px]">
                <div className="flex items-baseline gap-2 flex-wrap">
                    <strong className="font-semibold text-gray-800 dark:text-gray-100">{v.name || 'Medication'}</strong>
                    <span className="text-[11px] text-gray-400 dark:text-gray-500">
                        {[v.strength, v.dose, v.route].filter(Boolean).join(' · ')}
                    </span>
                    {earlier && (
                        <span className="text-[9px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-[#31373f] rounded px-1.5 py-0.5">
                            Earlier entry
                        </span>
                    )}
                </div>
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                    Scheduled {rec.scheduled_time || '—'}
                </p>
                <p className="text-[11px] text-gray-400 dark:text-gray-500">
                    {rec.recorded_by_name ? `${rec.recorded_by_name} · ` : ''}
                    Recorded {fmtDate(rec.dose_date)}{fmtClock(rec.recorded_at) ? ` at ${fmtClock(rec.recorded_at)}` : ''}
                </p>
                {rec.note && rec.note.trim() && (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{rec.note}</p>
                )}
            </div>
            <Badge kind={outcomeOf(rec.status)} label={rec.status} />
        </div>
    );
}

function ChangeRow({ change }) {
    const [open, setOpen] = useState(false);
    const fields = change.fields || [];
    return (
        <div className="py-3 text-[12px]">
            <p className="text-gray-700 dark:text-gray-200">
                <strong className="font-semibold">{change.name}</strong> · {change.action}
            </p>
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                Saved {fmtStamp(change.at)} · {change.who}
            </p>
            {change.effectiveAt && change.effectiveAt !== change.at && (
                <p className="text-[11px] text-gray-400 dark:text-gray-500">Takes effect: {fmtStamp(change.effectiveAt)}</p>
            )}
            {change.replacesPending > 0 && (
                <p className="text-[11px] text-gray-400 dark:text-gray-500">
                    Replaces {change.replacesPending} pending update{change.replacesPending === 1 ? '' : 's'}. Earlier versions retained.
                </p>
            )}
            {change.reason && <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{change.reason}</p>}
            {fields.length > 0 && (
                <>
                    <button onClick={() => setOpen((o) => !o)}
                        className="text-[11px] text-gray-400 dark:text-gray-500 hover:text-blue-600 transition mt-1.5">
                        View changed fields ({fields.length})
                    </button>
                    {open && (
                        <div className="grid gap-2 mt-2">
                            {fields.map((f) => (
                                <div key={f.label} className="bg-gray-50 dark:bg-[#20242c] rounded-md px-2.5 py-2">
                                    <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-200">{f.label}</p>
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500">Before: {f.before}</p>
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500">After: {f.after}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Add / edit form
// ---------------------------------------------------------------------------

function MedicationForm({
    form, setForm, setField, editingId, restoreMode, editingMed,
    now, today, error, preview, onFrequency, onTimes, onToggleDay,
}) {
    const prn = form.frequency === 'prn';
    const pending = editingMed ? pendingVersions(editingMed, now) : [];

    const courseSummary = (() => {
        if (!form.start) return '';
        if (form.ongoing) return `From ${fmtDate(form.start)} · Ongoing`;
        if (form.end && form.end >= form.start) {
            const days = courseLength(form.start, form.end);
            return `${days} calendar day${days === 1 ? '' : 's'} · Includes the end date.`;
        }
        return '';
    })();

    return (
        <div>
            {error && (
                <div className="flex items-start gap-2 text-[12px] text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/40 rounded-lg px-3 py-2.5 mb-4">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-px" /> <span>{error}</span>
                </div>
            )}

            {editingId && (
                <p className="text-[11px] text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/15 border border-blue-100 dark:border-blue-900/40 rounded-lg px-3 py-2.5 mb-4">
                    {restoreMode
                        ? 'Review the course and dose before restoring. Choose when it should resume below.'
                        : 'Choose when this change takes effect and check the next dose below. Previous records are retained.'}
                </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Medicine name *" value={form.name} onChange={setField('name')} placeholder="e.g. Paracetamol" />
                <Field label="Strength & form *" value={form.strength} onChange={setField('strength')} placeholder="e.g. 500 mg tablet" />
                <Field label="Dose to give *" value={form.dose} onChange={setField('dose')} placeholder="e.g. 1 tablet" />
                <div>
                    <label className={labelClass}>Route *</label>
                    <Select value={form.route} onChange={setField('route')} placeholder="Select route"
                        options={ROUTE_OPTIONS.map((r) => ({ value: r, label: r }))} className="w-full" />
                </div>
                <p className="sm:col-span-2 text-[11px] text-gray-400 dark:text-gray-500 -mt-1">
                    Enter medicine details exactly as prescribed.
                </p>
                <Field label="Condition / category" value={form.category} onChange={setField('category')} placeholder="e.g. Pain" />
                <div>
                    <label className={labelClass}>Instruction</label>
                    <Select value={form.instruction} onChange={setField('instruction')}
                        options={INSTRUCTION_OPTIONS.map((o) => ({ value: o, label: o }))} className="w-full" />
                </div>
                <div>
                    <label className={labelClass}>Frequency</label>
                    <Select value={form.frequency} onChange={onFrequency} options={FREQUENCY_OPTIONS} className="w-full" />
                </div>
                <div>
                    <label className={labelClass}>Status</label>
                    <Select value={form.status} onChange={setField('status')} options={VERSION_STATUS_OPTIONS} className="w-full" />
                </div>
            </div>

            {!prn && (
                <>
                    <div className="mt-3">
                        <TimesEditor times={form.times} onChange={onTimes} />
                    </div>
                    <div className="mt-3">
                        <label className={labelClass}>Days</label>
                        <div className="flex flex-wrap gap-1.5">
                            {DAYS.map((d) => (
                                <button key={d} type="button" onClick={() => onToggleDay(d)}
                                    className={`px-3 py-1.5 rounded-lg text-[12px] font-medium border transition ${form.days.includes(d)
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white dark:bg-[#262b33] text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700'}`}>
                                    {d}
                                </button>
                            ))}
                        </div>
                        {form.frequency === 'weekly' && (
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">A weekly medicine runs on one day — pick Custom to use more.</p>
                        )}
                    </div>
                </>
            )}

            {prn && (
                <div className="mt-3 bg-purple-50/50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-900/30 rounded-xl p-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Minimum interval (hours) *" type="number" min="0.25" step="0.25"
                            value={form.prnInterval} onChange={setField('prnInterval')} placeholder="As prescribed" />
                        <Field label="Maximum dose in 24 hours *" value={form.prnMaximum}
                            onChange={setField('prnMaximum')} placeholder="Amount and unit" />
                        <div className="sm:col-span-2">
                            <label className={labelClass}>When to give *</label>
                            <textarea value={form.prnWhen} onChange={(e) => setField('prnWhen')(e.target.value)}
                                placeholder="Prescribed reason and directions" rows={2}
                                className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#262b33] rounded-lg px-3 py-2 text-[13px] text-gray-700 dark:text-gray-300 placeholder-gray-400" />
                        </div>
                        <p className="sm:col-span-2 text-[11px] text-gray-400 dark:text-gray-500">
                            As-required medicines are not counted as scheduled daily doses.
                        </p>
                    </div>
                </div>
            )}

            {/* Course */}
            <div className="mt-3 bg-gray-50 dark:bg-[#20242c] border border-gray-100 dark:border-gray-800 rounded-xl p-3">
                <h3 className="text-[12px] font-semibold text-gray-700 dark:text-gray-200 mb-2.5">Medication course</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Start date *" type="date" value={form.start} onChange={setField('start')} />
                    <Field label={form.ongoing ? 'End date' : 'End date *'} type="date" min={form.start}
                        value={form.ongoing ? '' : form.end} onChange={setField('end')} disabled={form.ongoing} />
                </div>
                <label className="flex items-center gap-2 text-[12px] text-gray-600 dark:text-gray-300 mt-3 cursor-pointer">
                    <input type="checkbox" checked={form.ongoing}
                        onChange={(e) => setForm((f) => ({ ...f, ongoing: e.target.checked, end: e.target.checked ? '' : f.end }))}
                        className="w-4 h-4 accent-blue-600" />
                    Ongoing — no end date
                </label>
                {courseSummary && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">{courseSummary}</p>}
            </div>

            {/* Effective from — edits and restores only */}
            {editingId && (
                <div className="mt-3 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-xl p-3">
                    <h3 className="text-[12px] font-semibold text-gray-700 dark:text-gray-200 mb-2.5">Change takes effect from</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Effective date *" type="date" min={today}
                            value={form.effectiveDate} onChange={setField('effectiveDate')} />
                        <Field label="Effective time *" type="time"
                            min={form.effectiveDate === today ? now.slice(11) : undefined}
                            value={form.effectiveTime} onChange={setField('effectiveTime')} />
                    </div>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
                        {pending.length
                            ? `This replaces the pending update for ${fmtStamp(pending.at(-1).effectiveFrom)}. The existing schedule continues until the date and time chosen above. Both changes remain in History.`
                            : 'The existing schedule continues until this date and time.'}
                    </p>

                    {preview && (
                        <div className="grid gap-2 mt-3 pt-3 border-t border-blue-100 dark:border-blue-900/30">
                            <PreviewLine label="Last recorded dose" value={preview.given
                                ? `${fmtStamp(`${preview.given.dose_date}T${preview.given.scheduled_time || fmtClock(preview.given.recorded_at) || '00:00'}`)} · Given`
                                : 'No given dose recorded'} />
                            <PreviewLine label="Next scheduled dose"
                                value={preview.message || preview.next} note={preview.schedule} />
                            {preview.firstUpdated && (
                                <PreviewLine label="First dose on updated schedule" value={preview.firstUpdated} />
                            )}
                        </div>
                    )}

                    {preview && preview.transition && (
                        <p className="flex items-start gap-2 text-[11px] text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-900/40 rounded-lg px-3 py-2.5 mt-3 leading-relaxed">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" /> <span>{preview.transition}</span>
                        </p>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                <Field label="Prescribing doctor" value={form.doctor} onChange={setField('doctor')} placeholder="Dr. name" />
                <Field label="Notes" value={form.notes} onChange={setField('notes')} placeholder="Optional" />
            </div>

            {editingId && (
                <div className="mt-3">
                    <Field label="Reason for change *" value={form.changeReason} onChange={setField('changeReason')}
                        placeholder="Explain the update for the history" />
                </div>
            )}

            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-4">
                {editingId
                    ? `Saving adds a new version from ${form.effectiveDate ? fmtStamp(`${form.effectiveDate}T${form.effectiveTime}`) : 'the chosen time'}. Nothing already recorded is altered.`
                    : `Frequency: ${FREQUENCY_LABEL[form.frequency] || form.frequency}. Each administration time becomes its own dose on the carers' round.`}
            </p>
        </div>
    );
}

function PreviewLine({ label, value, note }) {
    return (
        <div className="flex items-start justify-between gap-4 text-[11px] text-gray-500 dark:text-gray-400">
            <span>{label}</span>
            <span className="text-right">
                <strong className="font-semibold text-gray-700 dark:text-gray-200">{value}</strong>
                {note && <span className="block text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{note}</span>}
            </span>
        </div>
    );
}

export default MedicationTab;
